import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLeafletContext } from '@react-leaflet/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FeatureGroup } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { CheckboxControl } from '../../../../../components/LeafletMap';
import { MAP_LEGEND } from '../../../../../lib/constants';
import { selectTeamCells } from '../../../../../redux';
import HeatMapLayer from './HeatMapLayer';
const WeeksSinceKnocked = () => {
    const refMap = {
        1: useRef(),
        2: useRef(),
        3: useRef(),
        4: useRef(),
        5: useRef(),
    };
    const context = useLeafletContext();
    const [selections, setSelections] = useState([]);
    const { teamCells } = useSelector(selectTeamCells);
    const getCheckboxControlLabel = (color, label) => (_jsxs("div", { className: "flex gap-x-2 justify-start", children: [_jsx("div", { style: { background: color, borderRadius: 2 }, className: "mt-0.5 w-3 h-3" }), _jsx("div", { className: "text-xs", children: label })] }));
    const controlOptions = [
        {
            label: getCheckboxControlLabel(MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[0].color, MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_LABELS[0]),
            value: MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[0],
        },
        {
            label: getCheckboxControlLabel(MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[1].color, MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_LABELS[1]),
            value: MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[1],
        },
        {
            label: getCheckboxControlLabel(MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[2].color, MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_LABELS[2]),
            value: MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[2],
        },
        {
            label: getCheckboxControlLabel(MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[3].color, MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_LABELS[3]),
            value: MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[3],
        },
        {
            label: getCheckboxControlLabel(MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[4].color, MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_LABELS[4]),
            value: MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[4],
        },
    ];
    const cells = useMemo(() => {
        if (!teamCells?.features) {
            return {};
        }
        const weeksSinceKnocked = {};
        weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[0]] = [];
        weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[1]] = [];
        weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[2]] = [];
        weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[3]] = [];
        weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[4]] = [];
        teamCells.features.forEach((cell) => {
            const average = cell.properties.avg_weeks_since_last_knocked;
            if (average === null) {
                weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[0]].push({
                    ...cell,
                    style: MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[0],
                });
            }
            else if (average <= 2) {
                weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[1]].push({
                    ...cell,
                    style: MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[1],
                });
            }
            else if (average >= 3 && average <= 5) {
                weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[2]].push({
                    ...cell,
                    style: MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[2],
                });
            }
            else if (average >= 6 && average <= 8) {
                weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[3]].push({
                    ...cell,
                    style: MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[3],
                });
            }
            else if (average >= 9) {
                weeksSinceKnocked[MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[4]].push({
                    ...cell,
                    style: MAP_LEGEND.WEEKS_SINCE_KNOCKED_CHOROPLETH_SCALE[4],
                });
            }
        });
        return weeksSinceKnocked;
    }, [teamCells]);
    useEffect(() => {
        const container = context.layerContainer || context.map;
        if (selections.indexOf(MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[0]) !== -1) {
            container.addLayer(refMap[1].current);
        }
        else {
            container.removeLayer(refMap[1].current);
        }
        if (selections.indexOf(MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[1]) !== -1) {
            container.addLayer(refMap[2].current);
        }
        else {
            container.removeLayer(refMap[2].current);
        }
        if (selections.indexOf(MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[2]) !== -1) {
            container.addLayer(refMap[3].current);
        }
        else {
            container.removeLayer(refMap[3].current);
        }
        if (selections.indexOf(MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[3]) !== -1) {
            container.addLayer(refMap[4].current);
        }
        else {
            container.removeLayer(refMap[4].current);
        }
        if (selections.indexOf(MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[4]) !== -1) {
            container.addLayer(refMap[5].current);
        }
        else {
            container.removeLayer(refMap[5].current);
        }
    }, [selections]);
    return (_jsxs("div", { children: [_jsx(CheckboxControl, { onChange: setSelections, options: controlOptions, defaultSelections: [
                    MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[0],
                    MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[1],
                    MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[2],
                    MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[3],
                    MAP_LEGEND.WEEKS_SINCE_KNOCKED_OPTIONS_KEYS[4],
                ] }), Object.keys(cells).map((key, i) => (_jsx(FeatureGroup, { ref: refMap[i + 1], children: _jsx(HeatMapLayer, { cells: cells[key] }) }, uuidv4())))] }));
};
export default WeeksSinceKnocked;
